import SelectSortBy from "@/components/census/header/SelectSortBy";
import SelectView from "@/components/census/header/SelectView";
import NavMenu from "@/components/NavMenu";
import { navigationNamespace } from "@/store/modules/navigation";
import { patientNamespace } from "@/store/modules/patient";
import { ViewConfig } from "@/store/modules/patient/config";
import { AudioAlertTracker } from "@/store/modules/patient/types";
import { userNamespace } from "@/store/modules/user";
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { FILTER_MY_PATIENTS, SORT_BY_ROOM } from "../shared/util";

@Component({
  components: {
    SelectView,
    SelectSortBy,
    NavMenu
  }
})
export default class CensusHeader extends Vue {
  @Action("censusPreference", navigationNamespace)
  censusPreference!: Function;

  @Action("showPopup", navigationNamespace)
  showPopup!: Function;

  @Getter("vars", patientNamespace)
  vars!: any;

  @Getter("towerMode", patientNamespace)
  towerMode!: boolean;

  @Getter("units", patientNamespace)
  units!: Array<any>;

  @Getter("demo", userNamespace)
  demo!: boolean;

  @Getter("selectedUnit", patientNamespace)
  selectedUnit!: string;

  @Getter("selectedView", patientNamespace)
  selectedView!: string;

  @Getter("selectedFilters", patientNamespace)
  selectedFilters!: string | null;

  @Getter("selectedSort", patientNamespace)
  selectedSort!: string;

  @Getter("area", patientNamespace)
  area!: string;

  @Getter("config", patientNamespace)
  config!: any;

  @Getter("audioAlerts", patientNamespace)
  audioAlerts!: Record<string, AudioAlertTracker>;

  @Getter("alertRepeatInterval", userNamespace)
  alertRepeatInterval!: number;

  get censusAudioAlerts() {
    const filteredResults: Record<string, AudioAlertTracker> = {};
    Object.entries(this.audioAlerts).forEach(([key, tracker]) => {
      if (tracker.alert.reviewStatus === 0) {
        filteredResults[key] = tracker;
        const room = tracker.patient.census.displayRoom
          .toString()
          .replace(/^OR-/, "");
        tracker.message = tracker.alert.audioMessage + " in room " + room;
      }
    });
    return filteredResults;
  }

  @Prop(Number)
  patientCount!: number;

  @Prop(Boolean)
  loadComplete!: boolean;

  extendToolbar = false;

  initialLoadComplete = false;
  @Watch("loadComplete")
  loaded() {
    if (this.loadComplete && !this.initialLoadComplete) {
      // Only call showDropdown() the first time loadComplete
      // becomes true (loadComplete switches from true to false every load)
      this.initialLoadComplete = true;
      this.showDropdown();
    }
  }

  showDropdown() {
    // If no results and only filter is "My Patients" open unit select
    if (
      this.patientCount === 0 &&
      this.selectedFilters === FILTER_MY_PATIENTS
    ) {
      const dropdown = this.$refs.selectUnits as any;
      dropdown.activateMenu();
    }
  }

  // all filters available for this view
  get filterItems(): { text: string; value: string }[] {
    if (this.vars && this.vars.uiFilters) {
      const items = this.vars.uiFilters
        .filter((m: any) => {
          if (this.towerMode) {
            return true;
          } else {
            return !m.tower;
          }
        })
        .map((m: any) => {
          return {
            text: m.display,
            value: m.name
          };
        });
      return items;
    } else {
      return [{ text: "Missing uiFilters config", value: "hasPatient" }];
    }
  }

  // v-model for filter drop down
  filterValues: Array<string> = [];

  // Display text for filter dropdown when not expanded
  get selectedFilterItems() {
    if (this.filterValues) {
      return this.filterValues
        .map((value: string) => {
          const foundItem = this.filterItems.find((item: any) => {
            return item.value === value;
          });
          if (foundItem) {
            return foundItem.text;
          }
        })
        .filter(m => m)
        .join(", ");
    } else {
      return "";
    }
  }

  updateUnit(unit: string) {
    // Remove my patients filter when navigating to a new unit
    this.filterValues = this.filterValues.filter(
      filter => filter !== FILTER_MY_PATIENTS
    );
    if (this.filterValues.length === 0) {
      //@ts-ignore
      this.$router.replace({ params: { unit, filters: undefined } });
    } else {
      this.$router.replace({
        params: { unit, filters: this.filterValues.join(",") }
      });
    }
  }

  clearFilters() {
    this.filterValues = [];
  }

  async closeFiltersDropDown() {
    if ((this.$refs.filterSelect as any).isMenuActive === false) {
      // already closed, don't do any work - caused by blur after user has clicked apply
      return;
    }
    (this.$refs.filterSelect as any).isMenuActive = false;

    const filters: string | undefined =
      this.filterValues.join(",") || undefined;

    // Can be called by close event or blur event
    // needs to guard against double navigation
    const from = this.$route.fullPath;
    // @ts-ignore Requires undefined to unset a param
    const to = this.$router.resolve({ params: { filters } }).route.fullPath;

    if (to !== from) {
      // @ts-ignore Requires undefined to unset a param
      this.$router.replace({ params: { filters } });
    }
  }

  @Watch("$route.path")
  async onSelectedChange() {
    (this.$refs.selectUnits as any).isMenuActive = false;

    this.updateFilters();

    this.censusPreference({
      view: this.selectedView,
      unit: this.selectedUnit,
      filters: this.selectedFilters,
      sort: this.selectedSort
    });
  }

  @Getter("hasError", patientNamespace)
  hasError!: boolean;

  @Getter("fullName", userNamespace)
  fullName!: string;

  @Getter("views", patientNamespace)
  views!: ViewConfig[];

  @Watch("towerMode")
  updateViews() {
    const routerReplace: any = {};
    // If view was availble in one mode but not the other, use the first one in the list
    if (
      !this.views.map(v => v.routerString).includes(this.$route.params.view)
    ) {
      if (this.views && this.views[0]) {
        if (!routerReplace.params) {
          routerReplace.params = {};
        }
        routerReplace.params.view = this.views[0].routerString;
      }
    }

    // If sort was availble in one mode but not the other, use the first one in the list
    if (!this.sortByItems.map(v => v.value).includes(this.$route.params.sort)) {
      if (this.sortByItems && this.sortByItems[0]) {
        if (!routerReplace.params) {
          routerReplace.params = {};
        }
        routerReplace.params.sort = this.sortByItems[0].value;
      }
    }

    // Look if any selected filters should be removed from the route
    // when sitching from tower
    const newFilterValues = this.filterValues.filter(i => {
      return this.filterItems.map(fi => fi.value).includes(i);
    });
    if (newFilterValues.length !== this.filterValues.length) {
      if (!routerReplace.params) {
        routerReplace.params = {};
      }
      routerReplace.params.filters = newFilterValues.join(",");
    }

    if (Object.keys(routerReplace).length !== 0) {
      this.$router.replace(routerReplace);
    }
  }

  mounted() {
    this.updateFilters();
    this.updateViews();
  }

  updateFilters() {
    this.filterValues =
      this.selectedFilters === null ? [] : this.selectedFilters.split(",");
  }

  get sortByItems(): { text: string; value: string }[] {
    if (this.vars && this.vars.uiSorts) {
      return this.vars.uiSorts
        .filter((m: any) => {
          if (this.towerMode) {
            return true;
          } else {
            return !m.tower;
          }
        })
        .map((m: any) => {
          return {
            text: m.display,
            value: m.name
          };
        });
    } else {
      return [{ text: "Missing uiSorts Config", value: SORT_BY_ROOM }];
    }
  }

  get disconnectedHeight() {
    return this.extendToolbar ? "top: 112px" : "top: 56px";
  }
}
